import { formatChatTime } from '@/shared/model/chat.model';
/** Build CRM create-room payload — matches `POST /api/chat-rooms`. */
export function buildCreateChatRoomRequest(name, currentUserId, memberUserIds, type = 'PUBLIC') {
    const adminId = currentUserId.trim();
    const others = [
        ...new Set(memberUserIds.map((id) => id.trim()).filter((id) => id && id !== adminId)),
    ];
    return {
        name: name.trim(),
        type,
        members: [
            { userId: adminId, role: 'ADMIN' },
            ...others.map((userId) => ({ userId, role: 'MEMBER' })),
        ],
    };
}
export function slugifyRoomKey(name) {
    return name
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-_]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}
/** Stable DM key from two participant user ids (sorted). */
export function buildDmRoomKey(userA, userB) {
    const [a, b] = [userA.trim(), userB.trim()].sort();
    return `dm-${slugifyRoomKey(a)}-${slugifyRoomKey(b)}`;
}
/** Chat member `userId` — login, email, or phone (matches `POST /api/chat-rooms` members). */
export function resolveIamUserId(user) {
    const login = user.login?.trim();
    if (login)
        return login;
    const email = user.email?.trim();
    if (email)
        return email;
    const contact = user.contactNumber?.trim();
    if (contact)
        return contact;
    const phoneUser = user.phoneUser?.trim();
    if (phoneUser)
        return phoneUser;
    const username = user.username?.trim();
    if (username)
        return username;
    if (user.id != null && user.id !== '')
        return String(user.id);
    return '';
}
/** Logged-in user id for chat-room membership (account login preferred). */
export function resolveCurrentChatUserId(session, crmProfile) {
    const fromAccount = session?.account?.login?.trim();
    if (fromAccount)
        return fromAccount;
    const fromSession = session?.username?.trim();
    if (fromSession)
        return fromSession;
    const fromProfileUser = crmProfile?.username?.trim();
    if (fromProfileUser)
        return fromProfileUser;
    const fromProfileEmail = crmProfile?.email?.trim();
    if (fromProfileEmail)
        return fromProfileEmail;
    const fromContact = crmProfile?.contactNumber?.trim();
    if (fromContact)
        return fromContact;
    return '';
}
export function userDisplayLabel(user) {
    const full = user.fullName?.trim();
    if (full)
        return full;
    const parts = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    if (parts)
        return parts;
    return user.username?.trim() || user.login?.trim() || 'User';
}
function normalizeChatUserId(raw) {
    return raw.trim().toLowerCase();
}
function chatUserIdsMatch(a, b) {
    const left = normalizeChatUserId(a);
    const right = normalizeChatUserId(b);
    if (!left || !right)
        return false;
    return left === right;
}
/** Opponent id/email/login in a DM room (excludes the logged-in user). */
export function resolveDmPeerUserId(room, currentUserId) {
    const self = currentUserId.trim();
    const name = room.name?.trim();
    if (name?.includes('&')) {
        const peer = name
            .split('&')
            .map((part) => part.trim())
            .find((part) => part && !chatUserIdsMatch(part, self));
        if (peer)
            return peer;
    }
    if (name && !chatUserIdsMatch(name, self)) {
        return name;
    }
    if (room.roomKey.startsWith('dm-')) {
        const selfSlug = slugifyRoomKey(self);
        const segments = room.roomKey.slice(3).split('-').filter(Boolean);
        if (segments.length >= 2) {
            for (let i = 0; i < segments.length; i += 1) {
                for (let j = i + 1; j < segments.length; j += 1) {
                    const candidate = `${segments.slice(i, j + 1).join('-')}`;
                    if (candidate && candidate !== selfSlug) {
                        return candidate.replace(/-/g, ' ');
                    }
                }
            }
        }
    }
    return name || room.roomKey;
}
/** Prefer IAM directory full name when the peer id matches a known user. */
export function resolveChatUserLabel(userId, directory) {
    const normalized = normalizeChatUserId(userId);
    if (!normalized)
        return userId;
    const match = directory?.find((user) => {
        const candidates = [
            resolveIamUserId(user),
            user.login,
            user.email,
            user.username,
            user.contactNumber,
            user.phoneUser,
            user.id != null ? String(user.id) : '',
        ]
            .map((value) => (typeof value === 'string' ? value.trim() : ''))
            .filter(Boolean);
        return candidates.some((candidate) => chatUserIdsMatch(candidate, normalized));
    });
    return match ? userDisplayLabel(match) : userId;
}
/** True when a directory user is already an active member of the room. */
export function isChatDirectoryUserInRoomMembers(user, members) {
    if (members.length === 0)
        return false;
    const candidates = [
        resolveIamUserId(user),
        user.login,
        user.email,
        user.username,
        user.contactNumber,
        user.phoneUser,
        user.id != null ? String(user.id) : '',
    ]
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .filter(Boolean);
    return members.some((member) => candidates.some((candidate) => chatUserIdsMatch(candidate, member.userId)));
}
/** STOMP topic for per-user room membership events (`MEMBER_ADDED`, …). */
export function buildChatUserTopic(username) {
    const trimmed = username.trim();
    if (!trimmed) {
        throw new Error('Chat user topic requires a username.');
    }
    return `/topic/chat.user.${trimmed}`;
}
export function parseChatUserRoomEvent(raw) {
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object')
            return null;
        const event = parsed;
        if (typeof event.eventType !== 'string')
            return null;
        const roomIdRaw = event.roomId;
        const roomId = typeof roomIdRaw === 'number'
            ? roomIdRaw
            : typeof roomIdRaw === 'string' && roomIdRaw.trim()
                ? Number(roomIdRaw)
                : NaN;
        if (!Number.isFinite(roomId))
            return null;
        return {
            eventType: event.eventType,
            roomId,
            roomKey: typeof event.roomKey === 'string' ? event.roomKey : undefined,
            name: typeof event.name === 'string' ? event.name : undefined,
            type: event.type === 'PUBLIC' || event.type === 'DM' || event.type === 'PRIVATE'
                ? event.type
                : undefined,
            myRole: typeof event.myRole === 'string' ? event.myRole : undefined,
            addedBy: typeof event.addedBy === 'string' ? event.addedBy : undefined,
            timestamp: typeof event.timestamp === 'string' ? event.timestamp : undefined,
        };
    }
    catch {
        return null;
    }
}
/** Map a `MEMBER_ADDED` STOMP event into sidebar `IChatRoom` shape. */
export function chatUserRoomEventToRoom(event) {
    return {
        id: event.roomId,
        roomKey: event.roomKey?.trim() || `CR-${String(event.roomId).padStart(3, '0')}`,
        name: event.name?.trim() || event.roomKey?.trim() || `Room ${event.roomId}`,
        type: event.type ?? 'PUBLIC',
        myRole: event.myRole,
        unreadCount: 0,
    };
}
export function getChatRoomDisplayName(room, currentUserId, directory) {
    if (room.type === 'DM') {
        const peerId = resolveDmPeerUserId(room, currentUserId);
        return resolveChatUserLabel(peerId, directory);
    }
    return room.name?.trim() || room.roomKey;
}
export function getChatRoomLastPreview(room) {
    const msg = room.lastMessage;
    if (!msg?.content)
        return null;
    return {
        preview: msg.content,
        time: formatChatTime(msg.createdAt),
    };
}
